import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { filter, map, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

type BreadcrumbItem = {
  url: string;
  labelKey: string;
};

const COMING_SOON_SECTION_KEY: Record<string, string> = {
  'bible-books': 'layout.comingSoon.sections.bibleBooks.title',
  'advanced-search': 'layout.comingSoon.sections.advancedSearch.title',
  'study-plans': 'layout.comingSoon.sections.studyPlans.title',
  highlights: 'layout.comingSoon.sections.highlights.title',
  'user-management': 'layout.comingSoon.sections.userManagement.title',
  settings: 'layout.comingSoon.sections.settings.title',
  analytics: 'layout.comingSoon.sections.analytics.title',
  'help-support': 'layout.comingSoon.sections.helpSupport.title',
  'manage-plan': 'layout.comingSoon.sections.managePlan.title',
};

function getPathSegments(url: string): string[] {
  const urlPath = url.split('?')[0]?.split('#')[0] ?? '';
  return urlPath.split('/').filter(Boolean);
}

function buildBreadcrumbs(url: string): BreadcrumbItem[] {
  const segments = getPathSegments(url);
  const primary = segments[0] ?? 'dashboard';
  const secondary = segments[1] ?? null;

  if (primary === 'dashboard' || primary === 'audio') {
    return [];
  }

  const crumbs: BreadcrumbItem[] = [
    { url: '/dashboard', labelKey: 'layout.sidebar.dashboard' },
  ];

  const current = mapSegmentToCrumb(primary, secondary);
  if (current) {
    crumbs.push(current);
  }

  return crumbs;
}

function mapSegmentToCrumb(
  primary: string,
  secondary: string | null
): BreadcrumbItem | null {
  switch (primary) {
    case 'passage':
      return { url: '/passage', labelKey: 'layout.sidebar.readBible' };
    case 'dictionary':
      return { url: '/dictionary', labelKey: 'layout.sidebar.dictionary' };
    case 'search':
      return { url: '/search', labelKey: 'layout.sidebar.search' };
    case 'theme':
    case 'themes':
      return { url: '/theme', labelKey: 'layout.sidebar.themes' };
    case 'coming-soon': {
      const sectionKey = secondary ? COMING_SOON_SECTION_KEY[secondary] : null;
      return {
        url: secondary ? `/coming-soon/${secondary}` : '/coming-soon',
        labelKey: sectionKey ?? 'layout.comingSoon.default.title',
      };
    }
    default:
      return null;
  }
}

@Component({
  selector: 'app-breadcrumbs',
  standalone: true,
  imports: [RouterModule, TranslocoDirective],
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsComponent {
  private readonly router = inject(Router);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly items = computed(() => buildBreadcrumbs(this.currentUrl()));
}
